import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useCoverDuties } from "@/hooks/useCoverDuties";
import TaskDetail from "@/components/tracker/TaskDetail";
import NoteViewer from "@/components/tracker/NoteViewer";

let emit = null;

// Open any task of the current workspace as a window over the current page.
// `list` (task ids in on-screen order) enables previous / next inside the window.
export function openTaskWindow(taskId, { onChanged, list } = {}) {
  if (emit) emit({ taskId, onChanged, list });
}

export const TASKS_CHANGED = "tasks:changed";

export function TaskWindowHost() {
  const { user } = useAuth();
  const { covers } = useCoverDuties();
  const [req, setReq] = useState(null);
  const [detail, setDetail] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    emit = setReq;
    return () => { emit = null; };
  }, []);

  useEffect(() => {
    if (!req) return;
    Promise.all([api.get(`/tasks/${req.taskId}`), api.get("/projects")])
      .then(([t, p]) => { setProjects(p.data); setDetail(t.data); })
      .catch((e) => { toast.error(formatApiError(e)); setReq(null); setDetail(null); });
  }, [req]);

  const list = req?.list || [];
  const index = detail ? list.indexOf(detail.id) : -1;
  const goTo = (i) => setReq((r) => ({ ...r, taskId: list[i] }));
  const nav = index >= 0 && list.length > 1
    ? { index, total: list.length, onPrev: index > 0 ? () => goTo(index - 1) : null, onNext: index < list.length - 1 ? () => goTo(index + 1) : null }
    : null;

  const changed = useCallback(() => {
    req?.onChanged?.();
    window.dispatchEvent(new Event(TASKS_CHANGED));
    window.dispatchEvent(new Event("review:refresh"));
  }, [req]);

  const projectOf = (t) => projects.find((p) => p.id === t?.project_id);
  const isLeadOf = (t) => {
    const p = projectOf(t);
    if (!p) return false;
    return (p.lead_ids || []).includes(user.id) || (!!t.group_id && (p.groups || []).some((g) => g.id === t.group_id && (g.lead_ids || []).includes(user.id)));
  };
  const isViewer = (t) => user.role !== "admin" && (projectOf(t)?.viewer_ids || []).includes(user.id);
  const canEdit = (t) => !isViewer(t) && (user.role === "admin" || isLeadOf(t) || t.owner_id === user.id || covers(t));
  const canEditNotes = (t) => user.role === "admin" || isLeadOf(t);

  if (!user) return null;
  return (
    <>
      <TaskDetail
        task={detail}
        projectName={projectOf(detail)?.name || ""}
        projectCode={projectOf(detail)?.code || ""}
        groupName={(projectOf(detail)?.groups || []).find((g) => g.id === detail?.group_id)?.name || ""}
        canEdit={detail ? canEdit(detail) : false}
        canManage={detail ? canEditNotes(detail) : false}
        nav={nav}
        onSaved={(fresh) => { if (fresh?.id) setDetail(fresh); changed(); }}
        onOpenDiscussion={(t) => { setDetail(null); setViewing(t); }}
        onClose={() => { setDetail(null); setReq(null); changed(); }}
      />
      <NoteViewer
        task={viewing}
        canEdit={viewing ? canEditNotes(viewing) : false}
        canReply={viewing ? !isViewer(viewing) : false}
        onSaved={changed}
        onClose={() => { setViewing(null); setReq(null); }}
      />
    </>
  );
}
